/**
 * Frontend-Overrides für Learning-Area-Texte (Title/Description/Sidebar/Badge),
 * ohne dass die Datenbank angepasst werden muss.
 *
 * Aktuell aktiv für slug "fachkraft-fuer-gitarre".
 */

// Bubble-Hintergrund-Bilder (einheitlicher Stil mit der Landingpage)
import liveWebinareImg from "@/assets/gitarre-portrait-laechelnd.jpg";
import begleitkurseImg from "@/assets/gitarre-akkord-detail.jpg";
import selbstlernkurseImg from "@/assets/gitarre-kindergruppe.jpg";
import weitereKurseImg from "@/assets/gitarre-singkreis.jpg";
import infosImg from "@/assets/gitarre-portrait-laechelnd.jpg";

export interface LearningAreaTextOverride {
  title?: string;
  description?: string;
  sidebarLabel?: string;
  badgeLabel?: string;
}

// Hinweis: Titel & Beschreibung werden direkt in der DB-Tabelle `learning_areas`
// gepflegt. Hier verbleiben nur Sidebar-/Badge-Labels, die nicht in der DB existieren.
const LEARNING_AREA_OVERRIDES: Record<string, LearningAreaTextOverride> = {
  "fachkraft-fuer-gitarre": {
    sidebarLabel: "Kita-Gitarrenkurs",
    badgeLabel: "Zertifikatskurs",
  },
  "fachkraft-fuer-musik-und-sprache": {
    sidebarLabel: "Musik & Sprache",
    badgeLabel: "Zertifikatskurs",
  },
};

export function getLearningAreaOverride(slug?: string | null): LearningAreaTextOverride {
  if (!slug) return {};
  return LEARNING_AREA_OVERRIDES[slug] ?? {};
}

/**
 * Wendet das Override auf ein Learning-Area-Objekt an. Felder bleiben erhalten,
 * wenn kein Override vorliegt. sidebarLabel/badgeLabel werden zusätzlich angehängt.
 */
export function applyLearningAreaOverride<
  T extends { slug?: string | null; title?: string | null; description?: string | null }
>(area: T): T & { sidebarLabel?: string; badgeLabel?: string } {
  const override = getLearningAreaOverride(area?.slug ?? null);
  if (!override.title && !override.description && !override.sidebarLabel && !override.badgeLabel) {
    return area;
  }
  return {
    ...area,
    title: override.title ?? area.title,
    description: override.description ?? area.description,
    sidebarLabel: override.sidebarLabel,
    badgeLabel: override.badgeLabel,
  };
}

/**
 * Pro Section (per section_type) ein eigener Intro-Header mit Bild + Erklärung.
 * Aktuell nur für "fachkraft-fuer-gitarre" definiert.
 */
export interface SectionIntro {
  image: string;
  title: string;
  description: string;
}

const SECTION_INTROS: Record<string, Record<string, SectionIntro>> = {
  "fachkraft-fuer-gitarre": {
    termine: {
      image: liveWebinareImg,
      title: "Deine Termine",
      description:
        "Hier findest du alle anstehenden Gitarren-Termine: die Live-Webinare des Kurses und die Liederwerkstatt Gitarrissimo – chronologisch sortiert.",
    },
    "courses:Begleitkurse zu den Live-Webinaren": {
      image: begleitkurseImg,
      title: "Begleitkurse zu den Live-Webinaren",
      description:
        "Zu jedem Live-Webinar gibt es einen passenden Begleitkurs mit Übungen, Noten und Zusatzmaterial. Schau dir die Inhalte vor oder nach dem Webinar in Ruhe an und hake den Kurs ab, sobald du ihn durchgearbeitet hast.",
    },
    "courses:Selbstlernkurse": {
      image: selbstlernkurseImg,
      title: "Deine Selbstlernkurse",
      description:
        "Diese Kurse bearbeitest du in deinem eigenen Tempo. Sie bauen Schritt für Schritt aufeinander auf – starte mit dem Gitarren-Kickstart, dann Akkorde Grund- und Aufbaukurs, danach Rhythmus & Zupfmuster. Der Barrégriffe-Kurs ist optional.",
    },
    "courses:Weitere Kurse": {
      image: weitereKurseImg,
      title: "Weitere Kurse",
      description:
        "Vertiefende Inhalte und Liedersammlungen rund um Gitarre & Kita-Alltag. Diese Kurse sind kein Pflichtbestandteil deiner Ausbildung, sondern eine Inspirationsquelle für deinen Berufsalltag.",
    },
    infos: {
      image: infosImg,
      title: "Weitere Infos",
      description:
        "Hilfreiche Hinweise, Hintergründe und Tipps rund um deinen Gitarrenkurs. Schau hier rein, wenn du Fragen zum Ablauf, zum Zertifikat oder zu deiner Ausbildung hast.",
    },
  },
};

/**
 * Liefert den Intro-Header für eine Section anhand von slug und (section_type|sectionTitle).
 * Lookup-Reihenfolge: "type:title" → "type" → null.
 */
export function getSectionIntro(
  slug: string | undefined | null,
  sectionType: string | undefined | null,
  sectionTitle?: string | null,
): SectionIntro | null {
  if (!slug || !sectionType) return null;
  const map = SECTION_INTROS[slug];
  if (!map) return null;
  if (sectionTitle) {
    const composite = `${sectionType}:${sectionTitle}`;
    if (map[composite]) return map[composite];
  }
  return map[sectionType] ?? null;
}
